import { WorkflowTypes } from './types';
import camelCase from 'lodash/fp/camelCase';
import intersection from 'lodash/intersection';
import { AgnosticCanvasWorkflowBuilderState } from 'src/reducers/workflowBuilderReducer/chatOrder';
import { CanvasWorkflowBuilderState } from 'src/slices/canvas-workflow-builder/workflowBuilderSlice';
import { Action, ContextVariable } from 'src/types/actionBuilderApiTypes';
import { SolveWidgetProduct } from 'src/types/types';
import {
  ActionStepList,
  ButtonsStepFields,
  MiniAutoflowPolicyStepFields,
  Step,
} from 'src/types/workflowBuilderAPITypes';
import { flattenCvs } from 'src/utils/actionBuilder/helpers';
import { StepTypes } from 'src/utils/enums';

export const getCamelCasedActionType = (action: Action | undefined) => {
  const actionType = action?.action_type || '';
  if (!actionType) {
    return undefined;
  }

  const camelCasedActionType = camelCase(actionType).substring(1);
  const dataAttributeName = `${
    actionType[0].toUpperCase() || ''
  }${camelCasedActionType}`;

  return dataAttributeName;
};

const bfs = (graph: Record<string, Step>, start: string) => {
  const queue = [start];
  const visited = new Set();
  const result: Record<string, unknown> = {};

  while (queue.length) {
    const vertex = queue.shift();

    if (vertex && !visited.has(vertex)) {
      visited.add(vertex);
      result[vertex] = graph[vertex].step_fields;

      for (const transition of graph[vertex].transitions) {
        const transitionStepId = transition.step_id;
        if (transitionStepId) {
          queue.push(transitionStepId);
        }
      }
    }
  }

  return result;
};

export const getCopiedIntentWorkflowData = (
  canvasData: CanvasWorkflowBuilderState,
  stepId: string,
) => {
  const handleConfigurationFieldsForActions = (
    canvasActionIdToActionComponent: { [k: string]: ActionStepList },
    stepIdToStepFields: Record<string, unknown>,
  ) => {
    const updatedCanvasActionIdToActionComponent: {
      [k: string]: ActionStepList;
    } = {};
    for (const actionKey of Object.keys(canvasActionIdToActionComponent)) {
      const value = { ...canvasActionIdToActionComponent[actionKey] };
      const entryStepId = value?.entry_step_id;

      if (entryStepId) {
        const configFields = value?.config_fields || {};
        const stepFieldsToAdd = stepIdToStepFields?.[entryStepId] || {};
        // stepFieldsToAdd should override configFields if both exists
        value.config_fields = {
          ...configFields,
          ...stepFieldsToAdd,
        };
      }
      updatedCanvasActionIdToActionComponent[actionKey] = value;
    }
    return updatedCanvasActionIdToActionComponent;
  };

  const {
    canvas_action_id_to_action_component: actionGraph,
    steps: stepGraph,
  } = canvasData;

  const stepIdToStepFields = bfs(stepGraph, stepId);
  const stepIds = Object.keys(stepIdToStepFields);
  const stepMap = Object.fromEntries(
    Object.entries(stepGraph).filter(([key]) => stepIds.includes(key)),
  );
  const canvasActionIdToActionComponent = Object.fromEntries(
    Object.entries(actionGraph).filter(([key]) => {
      return Boolean(intersection(actionGraph[key].steps, stepIds).length);
    }),
  );

  const updatedCanvasActionIdToActionComponent =
    handleConfigurationFieldsForActions(
      canvasActionIdToActionComponent,
      stepIdToStepFields,
    );

  const copiedIntentWorkflowData = {
    canvas_action_id_to_action_component:
      updatedCanvasActionIdToActionComponent,
    entry_step_id: stepId,
    step_map: stepMap,
  };
  return copiedIntentWorkflowData;
};

export const handlePasteGroupOfSteps = (
  canvasData: AgnosticCanvasWorkflowBuilderState,
  lastStepId: string | null,
  actualLastStepId: string | null,
) => {
  const {
    actionCaseMap,
    outputVariableValuesMap,
    selectedConditions,
    steps: stepGraph,
  } = canvasData;

  let parentTransitionId = '';
  // Prioritize getting parent transition id if it is a branch transition step
  if (actualLastStepId) {
    const stepType = stepGraph[actualLastStepId]?.step_type;
    const stepFields = stepGraph[actualLastStepId]?.step_fields as
      | ButtonsStepFields
      | MiniAutoflowPolicyStepFields;
    if (stepType === StepTypes.CONDITION) {
      const index = selectedConditions?.[actualLastStepId];
      parentTransitionId =
        stepGraph[actualLastStepId]?.transitions[index]?.transition_id ?? '';
    }
    if (
      stepType === StepTypes.BUTTONS ||
      stepType === StepTypes.MINI_AUTOFLOW_POLICY
    ) {
      const outputVariableName =
        stepType === StepTypes.MINI_AUTOFLOW_POLICY
          ? (stepFields as MiniAutoflowPolicyStepFields)
              ?.output_context_variable
          : (stepFields as ButtonsStepFields)?.output_variable;
      const outputValue =
        outputVariableName &&
        outputVariableValuesMap?.[actualLastStepId]?.[outputVariableName];
      if (outputValue) {
        const transition = stepGraph?.[actualLastStepId]?.transitions.find(
          transition =>
            transition.condition_expression?.values?.includes(outputValue),
        );
        if (transition) {
          parentTransitionId = transition.transition_id ?? '';
        }
      } else if (stepType === StepTypes.BUTTONS && lastStepId) {
        // Specific scenario for Dynamic article suggestion (pasting below transition)
        const isYesBranchChosen = actionCaseMap[lastStepId];
        const transitionBranches = stepGraph?.[actualLastStepId]?.transitions;
        const transitionId = isYesBranchChosen
          ? transitionBranches?.find(transition =>
              transition?.condition_expression?.values?.includes('yes_resolve'),
            )?.transition_id
          : transitionBranches?.find(transition =>
              transition?.condition_expression?.values?.includes('no_resolve'),
            )?.transition_id;
        if (transitionId) {
          parentTransitionId = transitionId;
        }
      }
    }
  }
  // On other stepTypes check for transition without step ids.
  if (!parentTransitionId && actualLastStepId) {
    const transitionLength =
      stepGraph?.[actualLastStepId]?.transitions?.length ?? 0;
    if (transitionLength === 1) {
      const transitionObj = stepGraph?.[actualLastStepId]?.transitions[0];
      parentTransitionId =
        transitionObj?.transition_id && !transitionObj?.step_id
          ? transitionObj.transition_id
          : '';
    }
  }

  const insertGroupPayload = {
    parent_step_id: actualLastStepId ?? '',
    parent_transition_id: parentTransitionId,
  };

  return insertGroupPayload;
};

export const findAllUnusableCvsInStepGraph = (
  stepGraph: Record<string, Step>,
  stepId: string,
  contextVariables: ContextVariable[],
) => {
  const flatCvs = flattenCvs(contextVariables);
  const findCvs = (value: string) => {
    const arr = [...value.matchAll(new RegExp('{{.*?}}', 'gi'))].map(a => {
      const cvId = a[0];

      const matchedCv = flatCvs.find(
        cv => cv.context_variable_id === cvId.slice(2, -2),
      )?.context_variable_id;

      if (!matchedCv) {
        return cvId.slice(2, -2);
      }
    });

    return arr.filter(Boolean);
  };

  const findCvsFromStepGraph = (graph: Record<string, Step>, start: string) => {
    const queue = [start];
    const visited = new Set();
    const result = new Set<string>();

    while (queue.length) {
      const vertex = queue.shift();

      if (vertex && !visited.has(vertex)) {
        visited.add(vertex);
        const stepFields = graph[vertex]?.step_fields as Record<
          string,
          unknown
        >;
        const message = (stepFields?.message ?? '') as string;
        if (message) {
          const cvs = findCvs(message) as string[];
          cvs.forEach(value => result.add(value));
        }
        const bodyParameters = (stepFields?.body_parameters ?? '') as string;
        if (bodyParameters) {
          const cvs = findCvs(bodyParameters) as string[];
          cvs.forEach(value => result.add(value));
        }
        const url = (stepFields?.url ?? '') as string;
        if (url) {
          const cvs = findCvs(url) as string[];
          cvs.forEach(value => result.add(value));
        }

        for (const transition of graph[vertex]?.transitions) {
          const transitionStepId = transition.step_id;
          if (transitionStepId) {
            queue.push(transitionStepId);
          }
        }
      }
    }

    return result;
  };

  return Array.from(findCvsFromStepGraph(stepGraph, stepId));
};

export const formatIntentNames = (intentNames: string[]) => {
  if (!intentNames.length) {
    return '';
  }

  if (intentNames.length === 1) {
    return intentNames.join(', ') + ' Workflow';
  }

  return intentNames.join(', ') + ' Workflows';
};

export const workflowTypeToProduct: Record<WorkflowTypes, SolveWidgetProduct> =
  {
    'api-live': 'api',
    'api-preview': 'api',
    email: 'interactive_email',
    'flamethrower-live': 'workflow_builder',
    'flamethrower-preview': 'workflow_builder',
    'interactive-email-live': 'interactive_email',
    'interactive-email-preview': 'interactive_email',
    'playbook-live': 'playbook',
    'playbook-preview': 'playbook',
    'slack-live': 'slack',
    'slack-preview': 'slack',
    'voice-live': 'voice',
    'voice-preview': 'voice',
    'web-call-live': 'web_call',
    'web-call-preview': 'web_call',
  };
